import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useMemo, useState} from "react";
import {containerClustersSelector, useGetAllContainerClustersQuery} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {
  ContainerClusterDataTable
} from "../../components/data-table/ContainerClusterDataTable/ContainerClusterDataTable";
import {HasAccess} from "../../components/auth/HasAccess/HasAccess";
import {useNavigate} from "react-router-dom";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {useAuth} from "../../providers/AuthProvider/AuthProvider";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";

export type ContainerClusterPageProps = {}

export const ContainerClusterDataTablePageRoute = () => {
  return (
    <ContainerClusterDataTablePage/>
  );
};

export const ContainerClusterDataTablePage = (props: ContainerClusterPageProps) => {

  const {t} = useTranslation(["translation", "common"]);
  const {permissions} = useAuth()
  const {regionId} = useApp();
  const navigate = useNavigate()

  const [access, setAccess] = useState(false);

  const selectContainerClusters = useMemo(() => {
    return containerClustersSelector();
  }, []);

  const {
    containerClusters,
    isLoading,
    refetch,
  } = useGetAllContainerClustersQuery(access ? (regionId ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      ...result,
      containerClusters: selectContainerClusters(result, undefined),
      isLoading: result.isLoading,
    })
  });

  return (
    <ProtectedPage permissions={["containercluster_view"]} isLoading={isLoading} onAccess={setAccess}>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title order={4}>
                      {t("common.entity.containercluster.plural", "Container Clusters", {ns: "common"})}
                    </Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t("common.actions.plural", "Actions", {ns: "common"})}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("common.actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <HasAccess permissions={["edit"]}>
                          <>
                            <Menu.Divider/>
                            <Menu.Item onClick={() => navigate("/container-clusters/form")}>
                              {t("common.action.new", "New", {ns: "common"})}
                            </Menu.Item>
                          </>
                        </HasAccess>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Card>
                <ContainerClusterDataTable
                  withBorder
                  records={containerClusters}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isLoading}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ProtectedPage>
  );
};
